import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import Snackbar from "@material-ui/core/Snackbar";
import { makeStyles } from "@material-ui/core/styles";

import ErrorIcon from "@material-ui/icons/ErrorOutline";
import SuccessIcon from "@material-ui/icons/CheckCircleOutline";
import WarningIcon from "@material-ui/icons/WarningOutlined";
import InfoIcon from "@material-ui/icons/InfoOutlined";

import { registerSnackbar } from "./snackbarService";

const SnackbarContext = createContext();
export const useSnackbarContext = () => useContext(SnackbarContext);

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minWidth: 280,
    maxWidth: "60vw",
    padding: "10px 16px",
    borderRadius: 8,
    color: "#fff",
    boxShadow:
      "0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12)",
    fontFamily: "sans-serif",
    fontSize: 14,
    lineHeight: "20px",
  },

  content: {
    display: "flex",
    alignItems: "flex-start",
    flex: 1,
    maxHeight: 180,
    overflowY: "auto",
  
    /* Firefox */
    scrollbarWidth: "thin",
    scrollbarColor: "#ffffff55 transparent", // thumb + track
  
    /* IE/Edge legacy */
    msOverflowStyle: "scrollbar",
  
    /* Chrome/Safari */
    "&::-webkit-scrollbar": {
      width: 6,
    },
  
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
  
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.4)",
      borderRadius: 10,
    },
  
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(255,255,255,0.6)",
    },
  },

  message: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  icon: {
    marginRight: 10,
    marginTop: 2,
    fontSize: 22,
    flexShrink: 0,
  },

  closeBtn: {
    marginLeft: 12,
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 16,
    background: "transparent",
    border: "none",
    color: "#fff",
  },
}));

export const SnackbarProvider = ({ children }) => {
  const classes = useStyles();

  const actionTimerRef = useRef(null);
  const maxTimerRef = useRef(null);

  const [state, setState] = useState({
    open: false,
    message: "",
    type: "success",
    duration: 3000,
    maxDuration: 300000,
    key: Date.now(),
  });

  const clearAllTimers = () => {
    clearTimeout(actionTimerRef.current);
    clearTimeout(maxTimerRef.current);
  };

  const clearSnackbar = useCallback(() => {
    clearAllTimers();
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const startAutoClose = useCallback(
    (duration) => {
      clearTimeout(actionTimerRef.current);

      actionTimerRef.current = setTimeout(() => {
        clearSnackbar();
      }, duration);
    },
    [clearSnackbar]
  );

  const showSnackbar = useCallback(
    ({
      message,
      type = "success",
      duration = 3000,
      maxDuration = 300000,
      waitForAction = true,
    }) => {
      clearAllTimers();

      setState({
        open: true,
        message,
        type,
        duration,
        maxDuration,
        key: Date.now(),
      });

      if (!waitForAction) {
        startAutoClose(duration);
      }

      maxTimerRef.current = setTimeout(() => {
        clearSnackbar();
      }, maxDuration);
    },
    [clearSnackbar, startAutoClose]
  );

  const triggerAutoClose = useCallback(() => {
    startAutoClose(state.duration);
  }, [startAutoClose, state.duration]);

  useEffect(() => {
    registerSnackbar(showSnackbar, clearSnackbar, triggerAutoClose);
    return () => clearAllTimers();
  }, [showSnackbar, clearSnackbar, triggerAutoClose]);

  const getColor = (type) => {
    switch (type) {
      case "success":
        return "#318500";
      case "error":
        return "#c71f25";
      case "warning":
        return "#F2AF17";
      case "info":
        return "#1976d2";
      default:
        return "#333";
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "success":
        return <SuccessIcon className={classes.icon} />;
      case "error":
        return <ErrorIcon className={classes.icon} />;
      case "warning":
        return <WarningIcon className={classes.icon} />;
      case "info":
        return <InfoIcon className={classes.icon} />;
      default:
        return null;
    }
  };

  return (
    <SnackbarContext.Provider
      value={{ showSnackbar, clearSnackbar, triggerAutoClose }}
    >
      {children}

      <Snackbar
        key={state.key}
        open={state.open}
        onClose={clearSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <div
          className={classes.root}
          style={{ backgroundColor: getColor(state.type) }}
        >
          <div className={classes.content}>
            {getIcon(state.type)}

            <div className={classes.message}>{state.message}</div>
          </div>

          <button className={classes.closeBtn} onClick={clearSnackbar}>
            ✕
          </button>
        </div>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};